import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import stripeApi from '../../../api/stripeApi';
import transactionApi from '../../../api/transactionApi';
import vendorApi from '../../../api/vendorApi';
import { LedgerViewMode, TransactionFilterParams } from '../../../api/types';

interface UseCashQueriesParams {
    ledgerViewMode: LedgerViewMode;
    canFetchLedgerData: boolean;
    cashFilters: TransactionFilterParams;
    scopeBusinessId?: string;
}

const useCashQueries = ({
    ledgerViewMode,
    canFetchLedgerData,
    cashFilters,
    scopeBusinessId,
}: UseCashQueriesParams) => {
    const { data: cashData, isLoading: isCashLoading, refetch: refetchCashData, isFetching: isCashFetching } = useQuery({
        queryKey: ['transactions', cashFilters],
        queryFn: () => transactionApi.getTransactions(cashFilters),
        placeholderData: (previousData) => previousData,
        enabled: ledgerViewMode === 'cash' && canFetchLedgerData,
    });

    const { data: linkedAccountsData } = useQuery({
        queryKey: ['linkedAccounts', scopeBusinessId],
        queryFn: () => stripeApi.getLinkedAccounts(scopeBusinessId),
        enabled: ledgerViewMode === 'cash' && !!scopeBusinessId,
        retry: false,
    });

    const { data: cashQboAccountsData } = useQuery({
        queryKey: ['cash-qbo-accounts', scopeBusinessId],
        queryFn: () => vendorApi.getQBOAccounts(scopeBusinessId),
        enabled: ledgerViewMode === 'cash' && !!scopeBusinessId,
    });

    const cashQboAccounts = useMemo(
        () => Object.values(cashQboAccountsData?.groups || {}).flat(),
        [cashQboAccountsData],
    );

    return {
        cashData,
        isCashLoading,
        refetchCashData,
        isCashFetching,
        linkedAccountsData,
        cashQboAccounts,
    };
};

export default useCashQueries;
